import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Skeleton,
  useTheme,
  useMediaQuery,
  Stack,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Layers as LayersIcon,
} from "@mui/icons-material";
import PermissionGuard from "../access-control/PermissionGuard";
import { useAccessControl } from "../../hooks/useAccessControl";
import { RESOURCES, ACTIONS } from "../../hooks/usePermissions";
import { useClassAllocations } from "../../hooks/useClassAllocations";
import { useAllocationFilters } from "../../hooks/useAllocationFilters";
import { useAllocationForm } from "../../hooks/useAllocationForm";
import AllocationFilterBar from "./AllocationFilterBar";
import AllocationsTable from "./AllocationsTable";
import AllocationForm from "./AllocationForm";
import FormDialog from "../common/FormDialog";
import AllocationDeleteDialog from "./AllocationDeleteDialog";
import BulkClassAllocationDialog from "./BulkClassAllocationDialog";
import TablePagination from "../common/TablePagination";

const ClassAllocations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.REPORT_CARD.CLASS_TEACHER_ALLOCATIONS, ACTIONS.READ);
  const canCreate = can(RESOURCES.REPORT_CARD.CLASS_TEACHER_ALLOCATIONS, ACTIONS.CREATE);

  const {
    allocations,
    loading,
    pagination,
    fetchAllocations,
    createAllocation,
    updateAllocation,
    deleteAllocation,
  } = useClassAllocations();

  const {
    formData,
    editingAllocation,
    showForm,
    openForm,
    resetForm,
    updateFormData,
  } = useAllocationForm();

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [allocationToDelete, setAllocationToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [allAssigned, setAllAssigned] = useState(false);

  const handleFilterChange = useCallback((filters) => {
    fetchAllocations(1, filters, true);
  }, [fetchAllocations]);

  const {
    teacherFilter,
    setTeacherFilter,
    classFilter,
    setClassFilter,
    streamFilter,
    setStreamFilter,
    yearFilter,
    setYearFilter,
    termFilter,
    setTermFilter,
    activeFilter,
    setActiveFilter,
    clearFilters,
    hasActiveFilters,
  } = useAllocationFilters(handleFilterChange);

  useEffect(() => {
    if (canRead) fetchAllocations();
  }, [fetchAllocations, canRead]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingAllocation) {
        await updateAllocation(editingAllocation.id, formData);
        setSnackbar({ open: true, message: 'Allocation updated successfully!', severity: 'success' });
      } else {
        await createAllocation(formData);
        setSnackbar({ open: true, message: 'Allocation created successfully!', severity: 'success' });
      }
      
      const currentFilters = {};
      if (teacherFilter) currentFilters.teacher_id = teacherFilter;
      if (classFilter) currentFilters.class_id = classFilter;
      if (streamFilter) currentFilters.stream_id = streamFilter;
      if (yearFilter) currentFilters.academic_year = yearFilter;
      if (termFilter) currentFilters.term_id = termFilter;
      if (activeFilter !== "") currentFilters.is_active = activeFilter;
      fetchAllocations(pagination.current_page, currentFilters, false);
      resetForm();
    } catch (error) {
      console.error("Error saving allocation:", error);
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
        setSnackbar({ open: true, message: errorMessages, severity: 'error' });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.message || error.message, severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setAllocationToDelete(allocations.find(a => a.id === id));
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteAllocation(allocationToDelete.id);
      const currentFilters = {};
      if (teacherFilter) currentFilters.teacher_id = teacherFilter;
      if (classFilter) currentFilters.class_id = classFilter;
      if (streamFilter) currentFilters.stream_id = streamFilter;
      if (yearFilter) currentFilters.academic_year = yearFilter;
      if (termFilter) currentFilters.term_id = termFilter;
      if (activeFilter !== "") currentFilters.is_active = activeFilter;
      fetchAllocations(pagination.current_page, currentFilters, false);
      setSnackbar({ open: true, message: 'Allocation deleted successfully!', severity: 'success' });
    } catch (error) {
      console.error("Error deleting allocation:", error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to delete allocation', severity: 'error' });
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setAllocationToDelete(null);
    }
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view class allocations.</Alert>;

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={200} height={20} />
          </Box>
          <Skeleton variant="rectangular" width={140} height={36} sx={{ borderRadius: 1 }} />
        </Box>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {[...Array(6)].map((_, i) => (
                <Grid item size={{ lg: 2, md: 4, sm: 6, xs: 12 }} key={i}>
                  <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Skeleton variant="text" width={120} height={24} sx={{ mb: 2 }} />
            <Paper elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    {[...Array(5)].map((_, i) => (
                      <TableCell key={i}>
                        <Skeleton variant="text" width={80} height={16} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(8)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(5)].map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" width={80} height={16} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={4}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item size={{ lg: 8, md: 8, sm: 12, xs: 12 }}>
            <Typography variant="h4" gutterBottom>
              Class Teacher Allocations
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage class teacher assignments
            </Typography>
          </Grid>
          <Grid item size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent={{ xs: "stretch", md: "flex-end" }} mt={{ xs: 2, md: 0 }}>
              {canCreate && <Button variant="outlined" startIcon={<LayersIcon />} onClick={() => setBulkDialog(true)} size="small" fullWidth={isMobile}>Bulk Assign</Button>}
              {canCreate && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => openForm()}
                  size="small"
                  fullWidth={isMobile}
                >
                  Add Allocation
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <AllocationFilterBar
        teacherFilter={teacherFilter}
        onTeacherChange={setTeacherFilter}
        classFilter={classFilter}
        onClassChange={setClassFilter}
        streamFilter={streamFilter}
        onStreamChange={setStreamFilter}
        yearFilter={yearFilter}
        onYearChange={setYearFilter}
        termFilter={termFilter}
        onTermChange={setTermFilter}
        activeFilter={activeFilter}
        onActiveFilterChange={setActiveFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters()}
        totalCount={pagination.total}
        isMobile={isMobile}
      />

      <FormDialog
        open={showForm}
        onClose={resetForm}
        title={editingAllocation ? "Edit Allocation" : "Add New Allocation"}
        onSubmit={handleSubmit}
        saving={saving}
        isMobile={isMobile}
        disabled={allAssigned}
      >
        <AllocationForm
          formData={formData}
          onFormChange={updateFormData}
          onAllAssignedChange={setAllAssigned}
        />
      </FormDialog>

      <AllocationDeleteDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        allocation={allocationToDelete}
        deleting={deleting}
      />

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center">
              <AssignmentIcon color="primary" sx={{ mr: 1 }} />
              Allocations List
            </Typography>
          </Box>

          <AllocationsTable
            allocations={allocations}
            onEdit={openForm}
            onDelete={handleDelete}
          />

          <TablePagination
            pagination={pagination}
            onPageChange={(page) => {
              const currentFilters = {};
              if (teacherFilter) currentFilters.teacher_id = teacherFilter;
              if (classFilter) currentFilters.class_id = classFilter;
              if (streamFilter) currentFilters.stream_id = streamFilter;
              if (yearFilter) currentFilters.academic_year = yearFilter;
              if (termFilter) currentFilters.term_id = termFilter;
              if (activeFilter !== "") currentFilters.is_active = activeFilter;
              fetchAllocations(page, currentFilters, false);
            }}
            onPageSizeChange={(newPerPage) => {
              const currentFilters = {};
              if (teacherFilter) currentFilters.teacher_id = teacherFilter;
              if (classFilter) currentFilters.class_id = classFilter;
              if (streamFilter) currentFilters.stream_id = streamFilter;
              if (yearFilter) currentFilters.academic_year = yearFilter;
              if (termFilter) currentFilters.term_id = termFilter;
              if (activeFilter !== "") currentFilters.is_active = activeFilter;
              fetchAllocations(1, currentFilters, false, newPerPage);
            }}
            isMobile={isMobile}
          />
        </CardContent>
      </Card>
      
      <BulkClassAllocationDialog
        open={bulkDialog}
        onClose={() => setBulkDialog(false)}
        onSuccess={(msg, severity) => {
          setSnackbar({ open: true, message: msg, severity });
          fetchAllocations();
        }}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClassAllocations;
